import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import { loginPartner, checkPartnerApiHealth } from "../../services/api/partnerService";

// Colorful theme backgrounds
import pricingBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";

const PartnerLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Check API health on mount
  useEffect(() => {
    document.title = "Partner Login - AXOIX";
    checkPartnerApiHealth().then(setApiAvailable);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      setLoading(false);
      return;
    }

    try {
      if (apiAvailable) {
        const result = await loginPartner({
          email: formData.email,
          password: formData.password,
        });

        // Redirect to partner's dashboard
        if (result.dashboard_url) {
          window.location.href = result.dashboard_url;
        } else {
          // Default redirect to partner dashboard
          window.location.href = "https://partner.axoix.com/dashboard";
        }
      } else {
        // API not available - show message
        setError(
          "Partner login service is currently unavailable. Please try again later or contact support."
        );
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Login failed. Please check your credentials.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle="Partner Login" pagesub="Partner Program" />
            <section
              className="contact pt-100 pb-120 bg_img"
              style={{ backgroundImage: `url(${pricingBg})` }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-6">
                    <div
                      className="pricing-item xb-border bg_img"
                      style={{ backgroundImage: `url(${cardBg})`, padding: "50px 40px" }}
                    >
                      <div className="form-heading text-center mb-30">
                        <h3 className="title">Welcome Back, Partner!</h3>
                        <p className="sub-title clr-white">
                          Login to access your AXOIX Partner Dashboard
                        </p>
                      </div>

                      {error && (
                        <div
                          className="services-outcome-wrap mb-25"
                          style={{ borderColor: "#ff4444" }}
                        >
                          <p style={{ color: "#ff4444", margin: 0 }}>
                            <i className="fas fa-exclamation-circle"></i> {error}
                          </p>
                        </div>
                      )}

                      {apiAvailable === false && (
                        <div
                          className="services-outcome-wrap mb-25"
                          style={{ borderColor: "#ffaa00" }}
                        >
                          <p style={{ color: "#ffaa00", margin: 0 }}>
                            <i className="fas fa-info-circle"></i> Partner login service is being
                            set up. Check back soon!
                          </p>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="xb-contact-form-wrap">
                        <div className="xb-form-input mb-25">
                          <label className="clr-white mb-10">Email Address</label>
                          <input
                            type="email"
                            name="email"
                            placeholder="john@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="xb-form-input mb-25">
                          <label className="clr-white mb-10">Password</label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={showPassword ? "text" : "password"}
                              name="password"
                              placeholder="Enter your password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                position: "absolute",
                                right: "15px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "none",
                                border: "none",
                                color: "var(--color-white)",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                              ></i>
                            </button>
                          </div>
                        </div>

                        <div className="ul_li_between mb-25">
                          <label
                            className="clr-white"
                            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                          >
                            <input
                              type="checkbox"
                              style={{
                                marginRight: "8px",
                                width: "16px",
                                height: "16px",
                                accentColor: "var(--color-primary)",
                              }}
                            />
                            Remember me
                          </label>
                          <Link
                            to="#"
                            style={{ color: "var(--color-primary)", fontSize: "14px" }}
                            onClick={(e) => {
                              e.preventDefault();
                              alert(
                                "Password reset functionality coming soon. Please contact support@axoix.com"
                              );
                            }}
                          >
                            Forgot Password?
                          </Link>
                        </div>

                        <button
                          type="submit"
                          className="thm-btn agency-btn w-100"
                          disabled={loading}
                          style={{ justifyContent: "center" }}
                        >
                          <span className="text">
                            {loading ? (
                              <>
                                <i
                                  className="fas fa-spinner fa-spin"
                                  style={{ marginRight: "10px" }}
                                ></i>
                                Logging in...
                              </>
                            ) : (
                              "Login to Dashboard"
                            )}
                          </span>
                          {!loading && (
                            <span className="arrow">
                              <span className="arrow-icon">
                                <svg
                                  width="28"
                                  height="28"
                                  viewBox="0 0 28 28"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    x="5.06592"
                                    y="19.9785"
                                    width="20.5712"
                                    height="2.61221"
                                    transform="rotate(-40.2798 5.06592 19.9785)"
                                    fill="white"
                                  />
                                  <rect
                                    x="7.97095"
                                    y="7.24463"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 7.97095 7.24463)"
                                    fill="white"
                                  />
                                  <rect
                                    x="11.6523"
                                    y="7.54834"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 11.6523 7.54834)"
                                    fill="white"
                                  />
                                  <rect
                                    x="15.334"
                                    y="7.85205"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 15.334 7.85205)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.7119"
                                    y="11.8374"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.7119 11.8374)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.4084"
                                    y="15.52"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.4084 15.52)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.104"
                                    y="19.2012"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.104 19.2012)"
                                    fill="white"
                                  />
                                </svg>
                                <svg
                                  width="28"
                                  height="28"
                                  viewBox="0 0 28 28"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    x="5.06592"
                                    y="19.9785"
                                    width="20.5712"
                                    height="2.61221"
                                    transform="rotate(-40.2798 5.06592 19.9785)"
                                    fill="white"
                                  />
                                  <rect
                                    x="7.97095"
                                    y="7.24463"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 7.97095 7.24463)"
                                    fill="white"
                                  />
                                  <rect
                                    x="11.6523"
                                    y="7.54834"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 11.6523 7.54834)"
                                    fill="white"
                                  />
                                  <rect
                                    x="15.334"
                                    y="7.85205"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 15.334 7.85205)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.7119"
                                    y="11.8374"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.7119 11.8374)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.4084"
                                    y="15.52"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.4084 15.52)"
                                    fill="white"
                                  />
                                  <rect
                                    x="18.104"
                                    y="19.2012"
                                    width="2.61221"
                                    height="2.61221"
                                    transform="rotate(-40.2798 18.104 19.2012)"
                                    fill="white"
                                  />
                                </svg>
                              </span>
                            </span>
                          )}
                        </button>
                      </form>

                      <div className="text-center mt-30">
                        <p className="clr-white">
                          Not a partner yet?{" "}
                          <Link to="/become-partner" style={{ color: "var(--color-primary)" }}>
                            Join our Partner Program
                          </Link>
                        </p>
                        <p className="clr-white mt-15" style={{ fontSize: "14px", opacity: 0.7 }}>
                          Earn up to 20% commission on every referral!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default PartnerLogin;
